import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MonthlyHeader from './MonthlyHeader';
import MonthlySummaryBox from './MonthlySummaryBox';
import ExpandableItem from '../../components/MasterComponent/ExpandableItem/ExpandableItem';
import Loading from '../../components/Loading/Loading';

type ServiceItem = {
  name: string;
  rate: string;
  usage: string;
  amount: string;
};

type Region = {
  regionName: string;
  items: ServiceItem[];
};

type ServiceData = {
  serviceName: string;
  total: string;
  regions: Region[];
};

type AccountData = {
  accountId: string;
  accountName: string;
  total: string;
  services: ServiceData[];
};

interface MonthlyBillingViewProps {
  accountsData: AccountData[];
  isLoading: boolean;
  isExpanded: boolean;
  openAccounts: Record<string, boolean>;
  openRegions: Record<string, Record<string, boolean>>;
  setOpenRegions: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, boolean>>>
  >;
  onToggleAll: () => void;
  onToggleAccount: (serviceName: string) => void;
}

const MonthlyBillingView: React.FC<MonthlyBillingViewProps> = ({
  accountsData,
  isLoading,
  isExpanded,
  openAccounts,
  openRegions,
  setOpenRegions,
  onToggleAll,
  onToggleAccount,
}) => {
  // Check if data is empty or null
  const hasData = Array.isArray(accountsData) && accountsData.length > 0;

  const totalAmount = useMemo(() => {
    if (!hasData) return 0;
    return accountsData.reduce((accountSum, account) => {
      const total = parseFloat(account.total?.replace(/[^0-9.-]+/g, '') || '0');
      return accountSum + total;
    }, 0);
  }, [accountsData]);

  const toggleRegion = (
    accountId: string,
    serviceName: string,
    regionName: string
  ) => {
    setOpenRegions((prev) => ({
      ...prev,
      [`${accountId}-${serviceName}`]: {
        ...(prev[`${accountId}-${serviceName}`] || {}),
        [regionName]: !prev[`${accountId}-${serviceName}`]?.[regionName],
      },
    }));
  };

const regionTotals = useMemo(() => {
  if (!Array.isArray(accountsData)) return {};

  const totals: Record<string, Record<string, Record<string, number>>> = {};

  accountsData.forEach((account) => {
    account.services.forEach((service) => {
      if (!totals[account.accountId]) totals[account.accountId] = {};
      if (!totals[account.accountId][service.serviceName])
        totals[account.accountId][service.serviceName] = {};

      service.regions.forEach((region) => {
        const total = region.items.reduce((sum, item) => {
          const num = parseFloat(item.amount?.replace(/[^0-9.-]+/g, '') || '0');
          return sum + num;
        }, 0);
        totals[account.accountId][service.serviceName][region.regionName] = total;
      });
    });
  });

  return totals;
}, [accountsData]);
  
  return (
    <Box>
      <MonthlyHeader
        isExpanded={isExpanded}
        onToggleAll={onToggleAll}
        disabled={isLoading || !hasData}
      />
      {isLoading ? (
        <Box
          height="60vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      ) : (
        <>
          <MonthlySummaryBox total={`$${totalAmount.toFixed(2)}`} />
          {hasData ? (
            accountsData.map((account) => (
              <ExpandableItem
                key={account.accountId}
                title={`${account.accountName} (${account.accountId})`}
                totalText={account.total}
                isOpen={openAccounts[account.accountId] ?? false}
                onToggle={() => onToggleAccount(account.accountId)}
              >
                {account.services.map((service) => {
                  const isOpen =
                    openAccounts[
                      `${account.accountId}-${service.serviceName}`
                    ] ?? false;

                  return (
                    <ExpandableItem
                      key={service.serviceName}
                      title={service.serviceName}
                      totalText={service.total}
                      isOpen={isOpen}
                      onToggle={() =>
                        onToggleAccount(
                          `${account.accountId}-${service.serviceName}`
                        )
                      }
                    >
                      {service.regions.map((region) => {
                        const regionTotal =
                          regionTotals[account.accountId]?.[
                            service.serviceName
                          ]?.[region.regionName] ?? 0;
                        const isRegionOpen =
                          openRegions[
                            `${account.accountId}-${service.serviceName}`
                          ]?.[region.regionName] ?? false;

                        return (
                          <ExpandableItem
                            key={region.regionName}
                            title={region.regionName}
                            totalText={`$${regionTotal.toFixed(2)}`}
                            isOpen={isRegionOpen}
                            onToggle={() =>
                              toggleRegion(
                                account.accountId,
                                service.serviceName,
                                region.regionName
                              )
                            }
                          >
                            {region.items.map((regionItem, idx) => (
                              <Box key={idx} className="region-item">
                                <div className="region-left">
                                  <Typography className="region-name">
                                    {regionItem.name}
                                  </Typography>
                                  <Typography className="region-amount">
                                    {regionItem.amount}
                                  </Typography>
                                </div>
                                <div className="flex-row">
                                  <Typography className="region-rate">
                                    {regionItem.rate}
                                  </Typography>
                                  <Typography className="region-amount-rate">
                                    {regionItem.amount}
                                  </Typography>
                                </div>
                              </Box>
                            ))}
                          </ExpandableItem>
                        );
                      })}
                    </ExpandableItem>
                  );
                })}
              </ExpandableItem>
            ))
          ) : (
            <div style={{ height: '100%' }} className="noDataCenterBox">
              No data available
            </div>
          )}
        </>
      )}
    </Box>
  );
};

export default MonthlyBillingView;
